'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePortal } from './portal.js';

/**
 * Offset en px entre el trigger y el panel flotante
 */
const PANEL_OFFSET = 8;

/**
 * Icono de edificio (cuenta) - SVG inline para evitar dependencias externas
 */
const BuildingIcon = ({ className = '' }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
    <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
    <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
  </svg>
);

/**
 * Chevron hacia abajo
 */
const ChevronDownIcon = ({ className = '' }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

/**
 * Lupa de búsqueda
 */
const SearchIcon = ({ className = '' }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

/**
 * Check para la cuenta seleccionada
 */
const CheckIcon = ({ className = '' }) => (
  <svg
    className={className}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

/**
 * Renderizar el icono de una cuenta.
 * Formas soportadas:
 * - Elemento React
 * - Función/componente React
 * - Objeto { component: ReactComponent }
 * - Objeto { url, alt } -> imagen
 * - Fallback: icono de edificio
 *
 * No se acepta HTML/SVG crudo a propósito: `accounts` suele venir de una API y
 * un `dangerouslySetInnerHTML` aquí sería un vector de XSS. Para SVG propio,
 * pasar un componente React.
 */
const renderAccountIcon = (icon, fallbackAlt) => {
  if (!icon) return <BuildingIcon />;

  if (React.isValidElement(icon)) return icon;

  if (typeof icon === 'function') {
    const IconComponent = icon;
    return <IconComponent />;
  }

  if (typeof icon === 'object') {
    if (icon.component) {
      const IconComponent = icon.component;
      return <IconComponent />;
    }
    if (icon.url) {
      return (
        <img
          src={icon.url}
          alt={icon.alt || fallbackAlt || 'account'}
          className="sidenav-account-avatar-image"
        />
      );
    }
  }

  return <BuildingIcon />;
};

/**
 * Normalizar el texto visible de una cuenta.
 * Acepta `label` (preferido) o `displayName`/`name` por comodidad del consumidor.
/**
 * Normalizar el texto visible de una cuenta.
 * Acepta `label` (preferido) o `displayName`/`name` por comodidad del consumidor.
 */
const getAccountLabel = (account) => {
  if (!account) return '';
  return account.label || account.displayName || account.name || account.id || '';
};

/**
 * Componente AccountSelector
 *
 * Selector de cuentas para el Sidenav. La lista de cuentas y la cuenta
 * seleccionada las inyecta el proyecto consumidor, y la selección se notifica
 * por el callback `onSelect`. Así cada proyecto implementa su propia lógica
 * (persistencia, refetch, analítica, recarga, etc.).
 *
 * Dos modos, según la cantidad de cuentas:
 * - 1 cuenta  -> valor estático (icono + nombre), sin menú.
 * - 2+ cuentas -> botón que abre un menú con búsqueda, filtros y lista.
 *
 * El menú se renderiza inline debajo del trigger cuando el sidenav está abierto,
 * y como panel flotante cuando está colapsado (donde el ancho no da para inline).
 *
 * No usa CSS Modules a propósito: el build UMD desactiva CSS Modules, por lo que
 * todas las clases son globales con prefijo `sidenav-account-`.
 */
let accountSelectorCount = 0;

const AccountSelector = ({
  accounts = [],
  selectedAccountId = null,
  // Objeto de la cuenta seleccionada. Útil con APIs paginadas: el trigger la
  // muestra aunque no venga en el lote actual (y aunque el id aún no se conozca).
  selectedAccount: selectedAccountProp = null,
  onSelect = null,
  open = false,
  onOpenChange = null,
  loading = false,
  error = null,
  searchable = true,
  filters = null,
  selectedFilterId = null,
  onFilterChange = null,
  allFilterId = 'all',
  // Búsqueda: 'local' filtra en cliente sobre `accounts`; 'remote' delega la
  // búsqueda al proyecto (típico cuando la API pagina y solo devuelve un lote).
  searchMode = 'local',
  onSearchChange = null,
  searchDebounceMs = 300,
  fetching = false,
  total = null,
  sidenavState = 'open',
  // Badges de la cuenta elegida en el trigger. El Sidenav los apaga cuando el
  // toggle de fuente está visible: ahí las fuentes ya se ven como opciones y
  // repetirlas como badges es ruido.
  showTriggerBadges = true,
  t = (key, fallback) => fallback || key,
  className = '',
  // Clases del contenedor del panel flotante, que se renderiza en un portal
  // (fuera del Sidenav). El Sidenav pasa su clase de tema para que el panel
  // conserve los colores del modo oscuro.
  portalClassName = '',
}) => {
  const inPortal = usePortal();
  // Id estable por instancia (sin useId: el Sidenav soporta React >= 16.8).
  const listboxIdRef = useRef(null);
  if (listboxIdRef.current === null) {
    accountSelectorCount += 1;
    listboxIdRef.current = `sidenav-account-listbox-${accountSelectorCount}`;
  }
  const listboxId = listboxIdRef.current;
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [position, setPosition] = useState(null);

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);

  const isCollapsed = sidenavState === 'collapsed';
  const safeAccounts = Array.isArray(accounts) ? accounts : [];
  const safeFilters = Array.isArray(filters) ? filters : null;

  // Caso A del AC: con una sola cuenta (o ninguna) el bloque es estático.
  //
  // Con `searchMode="remote"` la lista que llega es el RESULTADO de la búsqueda,
  // no cuántas cuentas tiene el usuario: al teclear puede quedar en 1 y el
  // selector no debe convertirse en estático a media búsqueda (cerraría el menú).
  // Por eso se mantiene interactivo mientras el panel está abierto o hay término.
  // Id con el que se decide qué fila está seleccionada. Cuando el consumidor no
  // puede resolver el id porque la cuenta no viene en el lote, se usa el del
  // objeto `selectedAccount`, que es justo el que se ancla al tope de la lista.
  const effectiveSelectedId =
    selectedAccountId ?? selectedAccountProp?.id ?? null;

  const hasTotal = typeof total === 'number' && total >= 0;
  const isRemote = searchMode === 'remote';

  // En modo remoto `accounts` es una PÁGINA de resultados, así que su tamaño no
  // dice cuántas cuentas tiene el usuario: se usa `total` si el consumidor lo
  // pasa y, si no, se asume interactivo (no se puede demostrar lo contrario).
  // En modo local `accounts` sí es la lista completa, que es el supuesto del
  // caso A del AC.
  const isInteractive = isRemote
    ? (hasTotal ? total > 1 : true)
    : safeAccounts.length > 1 || open || query.trim() !== '';
  const isOpen = open && isInteractive;

  // El panel flota solo en estado colapsado; abierto va inline
  const isFloating = isCollapsed;

  // Estado del filtro: controlado por el consumidor si manda `selectedFilterId`,
  // interno en caso contrario. Así cumplir el AC no obliga a cada proyecto a
  // cablear estado.
  const isFilterControlled =
    selectedFilterId !== null && selectedFilterId !== undefined;
  const [internalFilterId, setInternalFilterId] = useState(allFilterId);
  const activeFilterId = isFilterControlled ? selectedFilterId : internalFilterId;

  // Cuenta seleccionada actual
  // La cuenta seleccionada se busca en la lista actual, pero con búsqueda remota
  // esa lista es un lote: la cuenta elegida puede no estar en él al reabrir el
  // menú. Se cachea la última conocida para que el trigger la siga mostrando.
  const lastSelectedRef = useRef(null);

  const selectedAccount = useMemo(() => {
    const found =
      safeAccounts.find((account) => account.id === selectedAccountId) || null;
    if (found) {
      lastSelectedRef.current = found;
      return found;
    }
    // Cache válida solo si sigue siendo la misma cuenta seleccionada
    if (selectedAccountId && lastSelectedRef.current?.id === selectedAccountId) {
      return lastSelectedRef.current;
    }
    // Último recurso: el objeto que pasa el consumidor
    return selectedAccountProp || null;
  }, [safeAccounts, selectedAccountId, selectedAccountProp]);

  const isRemoteSearch = isRemote;

  /**
   * Filtrado: scope Y búsqueda combinados con lógica AND.
   * - El filtro "todas" (`allFilterId`) no restringe por scope.
   * - Una cuenta sin `scopes` queda fuera de cualquier filtro específico.
   * - Con `searchMode="remote"` el texto NO se filtra aquí: la lista que llega
   *   ya viene filtrada por el servidor. El filtro por scope sigue siendo local
   *   porque se deriva de `scopes`, que lo provee el propio consumidor.
   */
  const filteredAccounts = useMemo(() => {
    const needle =
      searchable && !isRemoteSearch ? query.trim().toLowerCase() : '';
    const restrictScope = Boolean(
      safeFilters && activeFilterId && activeFilterId !== allFilterId
    );

    return safeAccounts.filter((account) => {
      if (restrictScope) {
        const scopes = Array.isArray(account.scopes) ? account.scopes : [];
        if (!scopes.includes(activeFilterId)) return false;
      }

      if (!needle) return true;

      const label = getAccountLabel(account).toLowerCase();
      const sublabel = (account.sublabel || '').toLowerCase();
      return label.includes(needle) || sublabel.includes(needle);
    });
  }, [
    safeAccounts,
    query,
    searchable,
    isRemoteSearch,
    safeFilters,
    activeFilterId,
    allFilterId,
  ]);

  /**
   * Lista final que se pinta.
   * Con APIs paginadas la cuenta seleccionada suele no venir en el lote: se
   * ancla al tope para que SIEMPRE se vea marcada. Mientras hay término de
   * búsqueda no se ancla, para no ensuciar los resultados.
   */
  const visibleAccounts = useMemo(() => {
    if (!selectedAccount) return filteredAccounts;
    if (query.trim() !== '') return filteredAccounts;
    const alreadyThere = filteredAccounts.some(
      (account) => account.id === selectedAccount.id
    );
    if (alreadyThere) return filteredAccounts;
    return [selectedAccount, ...filteredAccounts];
  }, [filteredAccounts, selectedAccount, query]);

  /**
   * Notificar la búsqueda al proyecto, con debounce.
   * Es el punto de extensión para búsqueda en servidor: el consumidor recibe el
   * término y decide cómo traer más cuentas.
   */
  useEffect(() => {
    if (!onSearchChange) return undefined;
    // No disparar la notificación mientras el panel está cerrado
    if (!isOpen) return undefined;

    const timer = setTimeout(() => {
      onSearchChange(query.trim());
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [query, isOpen, searchDebounceMs]);

  // Header "X of N accounts": X = posición de la cuenta seleccionada, N = total.
  // Con búsqueda remota `accounts` se encoge al buscar, así que el consumidor
  // puede pasar `total` con el total real; si no, se usa el tamaño de la lista.
  const selectedPosition =
    safeAccounts.findIndex((account) => account.id === selectedAccountId) + 1;
  const rawTotal =
    typeof total === 'number' && total >= 0 ? total : safeAccounts.length;
  // Al buscar, la lista puede quedar en 0 momentáneamente: mostrar "0 of 0"
  // es ruido, así que se conserva el último total conocido.
  const lastTotalRef = useRef(0);
  if (rawTotal > 0) lastTotalRef.current = rawTotal;
  const totalAccounts = rawTotal > 0 ? rawTotal : lastTotalRef.current;

  const setOpen = (next) => {
    if (!isInteractive) return;
    onOpenChange?.(next);
  };

  const closePanel = () => setOpen(false);

  /**
   * Calcular la posición del panel flotante respecto al trigger (solo colapsado).
   * Misma técnica que Popover: fixed + getBoundingClientRect con flip de viewport.
   */
  useEffect(() => {
    if (!isOpen || !isFloating) {
      setPosition(null);
      return undefined;
    }

    const computePosition = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;

      const triggerRect = trigger.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left = triggerRect.right + PANEL_OFFSET;
      let top = triggerRect.top;

      // Si no cabe a la derecha, abrir hacia la izquierda
      if (left + panelRect.width > viewportWidth) {
        left = triggerRect.left - panelRect.width - PANEL_OFFSET;
      }
      // Si tampoco cabe a la izquierda, pegar al borde
      if (left < PANEL_OFFSET) {
        left = PANEL_OFFSET;
      }
      // Ajustes verticales
      if (top + panelRect.height > viewportHeight) {
        top = viewportHeight - panelRect.height - PANEL_OFFSET;
      }
      if (top < PANEL_OFFSET) {
        top = PANEL_OFFSET;
      }

      setPosition({ top, left });
    };

    computePosition();

    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);

    return () => {
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    };
  }, [isOpen, isFloating, filteredAccounts.length, loading, error, safeFilters]);

  // Al abrir: limpiar búsqueda, resaltar la cuenta seleccionada y enfocar
  useEffect(() => {
    if (!isOpen) {
      // Avisar del reset solo si había búsqueda, para no provocar refetches
      // innecesarios en el consumidor.
      if (query) onSearchChange?.('');
      setQuery('');
      setHighlightIndex(-1);
      return;
    }

    const selectedIndex = safeAccounts.findIndex(
      (account) => account.id === selectedAccountId
    );
    setHighlightIndex(selectedIndex >= 0 ? selectedIndex : -1);

    if (searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    } else if (panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen]);

  // Al cambiar la búsqueda o el filtro, reiniciar el resaltado
  useEffect(() => {
    if (!isOpen) return;
    setHighlightIndex(visibleAccounts.length > 0 ? 0 : -1);
  }, [query, activeFilterId]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      const insidePanel = panelRef.current?.contains(event.target);
      const insideTrigger = triggerRef.current?.contains(event.target);
      if (!insidePanel && !insideTrigger) {
        closePanel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (account) => {
    if (!account || account.disabled) return;
    // Guardar ya el objeto: si el consumidor recarga otro lote, el trigger
    // sigue mostrando la cuenta que se acaba de elegir.
    lastSelectedRef.current = account;
    onSelect?.(account);
    setQuery('');
    closePanel();
  };

  const handleFilterChange = (filterId) => {
    if (!isFilterControlled) setInternalFilterId(filterId);
    onFilterChange?.(filterId);
  };

  /**
   * Navegación por teclado dentro del selector.
   * Escape detiene la propagación para que el Sidenav no se colapse.
   */
  const handleKeyDown = (event) => {
    if (!isInteractive) return;

    if (!isOpen) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closePanel();
      triggerRef.current?.focus();
      return;
    }

    if (!visibleAccounts.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((prev) => (prev + 1) % visibleAccounts.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((prev) =>
        prev <= 0 ? visibleAccounts.length - 1 : prev - 1
      );
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      setHighlightIndex(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setHighlightIndex(visibleAccounts.length - 1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const account = visibleAccounts[highlightIndex];
      if (account) handleSelect(account);
    }
  };

  // El trigger NO cambia de forma mientras el panel está abierto: al buscar,
  // react-query vuelve a `isLoading` (queryKey nueva) y el trigger se convertía
  // en un skeleton, moviendo todo el bloque.
  const showSkeleton = loading && safeAccounts.length === 0 && !isOpen;
  // Buscando: hay término y todavía no llegan resultados
  const isSearching = (loading || fetching) && query.trim() !== '';
  const highlightedAccount = visibleAccounts[highlightIndex] || null;
  const triggerLabel = getAccountLabel(selectedAccount);
  const placeholder = t('account.placeholder', 'Select account');

  /**
   * Renderizar el trigger: skeleton, valor estático (1 cuenta) o botón (2+).
   */
  const renderTrigger = () => {
    if (showSkeleton) {
      return (
        <output
          aria-busy="true"
          aria-label={t('account.loading', 'Loading selected account')}
          className={`sidenav-account-trigger sidenav-account-trigger--skeleton ${
            isCollapsed ? 'sidenav-account-trigger--collapsed' : ''
          }`}
        >
          <span className="sidenav-account-skeleton sidenav-account-skeleton--icon" />
          {!isCollapsed && (
            <span className="sidenav-account-skeleton sidenav-account-skeleton--text" />
          )}
        </output>
      );
    }

    // Caso A: una sola cuenta -> valor estático, sin menú
    if (!isInteractive) {
      const staticAccount = selectedAccount || safeAccounts[0] || null;
      const staticLabel = getAccountLabel(staticAccount) || placeholder;

      return (
        <div
          className={`sidenav-account-static ${
            isCollapsed ? 'sidenav-account-static--collapsed' : ''
          }`}
          title={staticLabel}
        >
          <span className="sidenav-account-trigger-icon">
            {renderAccountIcon(staticAccount?.icon, staticLabel)}
          </span>
          {!isCollapsed && (
            <>
              <span className="sidenav-account-trigger-name">{staticLabel}</span>
              {showTriggerBadges &&
                Array.isArray(staticAccount?.badges) &&
                staticAccount.badges.length > 0 && (
                  <span className="sidenav-account-trigger-badges">
                    {staticAccount.badges.map((badge, i) => (
                      <span
                        key={badge.id || badge.label || i}
                        className={`sidenav-account-badge sidenav-account-badge--${
                          badge.tone || 'default'
                        }`}
                      >
                        {badge.label}
                      </span>
                    ))}
                  </span>
                )}
            </>
          )}
        </div>
      );
    }

    // Caso B: dos o más cuentas -> botón con chevron
    return (
      <button
        ref={triggerRef}
        type="button"
        className={`sidenav-account-trigger ${
          isCollapsed ? 'sidenav-account-trigger--collapsed' : ''
        } ${isOpen ? 'sidenav-account-trigger--open' : ''}`}
        onClick={() => setOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={triggerLabel || placeholder}
      >
        <span className="sidenav-account-trigger-icon">
          {renderAccountIcon(selectedAccount?.icon, triggerLabel)}
        </span>
        {!isCollapsed && (
          <>
            <span className="sidenav-account-trigger-name">
              {triggerLabel || placeholder}
            </span>
            {/* Scope de la cuenta elegida (p. ej. GTS / GBS) */}
            {showTriggerBadges &&
              selectedAccount &&
              Array.isArray(selectedAccount.badges) &&
              selectedAccount.badges.length > 0 && (
                <span className="sidenav-account-trigger-badges">
                  {selectedAccount.badges.map((badge, i) => (
                    <span
                      key={badge.id || badge.label || i}
                      className={`sidenav-account-badge sidenav-account-badge--${
                        badge.tone || 'default'
                      }`}
                    >
                      {badge.label}
                    </span>
                  ))}
                </span>
              )}
            <span className="sidenav-account-trigger-chevron">
              <ChevronDownIcon />
            </span>
          </>
        )}
      </button>
    );
  };

  /**
   * Renderizar el contenido de la lista (error / loading / vacío / opciones)
   */
  const renderListContent = () => {
    if (error) {
      return (
        <div className="sidenav-account-error" role="alert">
          {error}
        </div>
      );
    }

    // Buscando: mensaje compacto con la misma huella que el estado vacío, para
    // que el panel no cambie de alto entre "buscando" y "sin resultados".
    if (isSearching && filteredAccounts.length === 0) {
      return (
        <div className="sidenav-account-searching" aria-busy="true">
          <span className="sidenav-account-spinner" aria-hidden="true" />
          <span>{t('account.searching', 'Searching…')}</span>
        </div>
      );
    }

    // Carga inicial (sin término): skeletons
    if (loading && filteredAccounts.length === 0) {
      return (
        <ul
          className="sidenav-account-list"
          aria-busy="true"
          aria-label={t('account.loadingList', 'Loading accounts')}
        >
          {[0, 1, 2, 3].map((index) => (
            <li key={index} className="sidenav-account-loading-row">
              <span className="sidenav-account-skeleton sidenav-account-skeleton--avatar" />
              <span className="sidenav-account-skeleton sidenav-account-skeleton--text" />
            </li>
          ))}
        </ul>
      );
    }

    if (visibleAccounts.length === 0) {
      return (
        <div className="sidenav-account-empty">
          <span className="sidenav-account-empty-icon">
            <SearchIcon />
          </span>
          <p className="sidenav-account-empty-title">
            {t('account.empty', 'No accounts found')}
          </p>
          <p className="sidenav-account-empty-hint">
            {t('account.emptyHint', 'Try a different search term')}
          </p>
        </div>
      );
    }

    return (
      <ul
        id={listboxId}
        className="sidenav-account-list"
        role="listbox"
        tabIndex={-1}
        aria-label={t('account.title', 'Switch Account')}
        aria-activedescendant={
          highlightedAccount
            ? `sidenav-account-option-${highlightedAccount.id}`
            : undefined
        }
      >
        {visibleAccounts.map((account, index) => {
          const isSelected = account.id === effectiveSelectedId;
          const isHighlighted = index === highlightIndex;
          const label = getAccountLabel(account);
          const badges = Array.isArray(account.badges) ? account.badges : [];

          const optionClasses = [
            'sidenav-account-option',
            isSelected ? 'sidenav-account-option--selected' : '',
            isHighlighted ? 'sidenav-account-option--highlighted' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <li key={account.id} role="none">
              <button
                id={`sidenav-account-option-${account.id}`}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={optionClasses}
                onClick={() => handleSelect(account)}
                onMouseEnter={() => setHighlightIndex(index)}
                disabled={account.disabled}
              >
                <span className="sidenav-account-avatar">
                  {renderAccountIcon(account.icon, label)}
                </span>

                {/* Nombre, sublabel y badges, cada uno en su propia línea */}
                <span className="sidenav-account-text">
                  <span className="sidenav-account-name">{label}</span>
                  {account.sublabel && (
                    <span className="sidenav-account-sublabel">
                      {account.sublabel}
                    </span>
                  )}
                  {badges.length > 0 && (
                    <span className="sidenav-account-badges">
                      {badges.map((badge, badgeIndex) => (
                        <span
                          key={badge.id || badge.label || badgeIndex}
                          className={`sidenav-account-badge sidenav-account-badge--${
                            badge.tone || 'default'
                          }`}
                        >
                          {badge.label}
                        </span>
                      ))}
                    </span>
                  )}
                </span>

                {isSelected && (
                  <span className="sidenav-account-check">
                    <CheckIcon />
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  /**
   * Renderizar el menú (inline cuando está abierto, flotante cuando colapsado)
   */
  const renderPanel = () => {
    if (!isOpen) return null;

    const panelClasses = [
      'sidenav-account-panel',
      isFloating
        ? 'sidenav-account-panel--floating'
        : 'sidenav-account-panel--inline',
    ].join(' ');

    const panelStyle = isFloating
      ? {
          position: 'fixed',
          top: position ? `${position.top}px` : 0,
          left: position ? `${position.left}px` : 0,
          visibility: position ? 'visible' : 'hidden',
        }
      : undefined;

    const panel = (
      <div
        ref={panelRef}
        className={panelClasses}
        tabIndex={-1}
        style={panelStyle}
      >
        <div className="sidenav-account-panel-header">
          <p className="sidenav-account-panel-count">
            {/* Si la seleccionada no está en el lote cargado no se conoce su
                posición real; mostrar "0 of N" sería ruido, así que solo el total. */}
            {selectedPosition > 0
              ? `${selectedPosition} ${t('account.of', 'of')} ${totalAccounts} ${t(
                  'account.countLabel',
                  totalAccounts === 1 ? 'account' : 'accounts'
                )}`
              : `${totalAccounts} ${t(
                  'account.countLabel',
                  totalAccounts === 1 ? 'account' : 'accounts'
                )}`}
            {/* Actividad mientras el servidor responde: la lista sigue visible */}
            {fetching && <span className="sidenav-account-spinner" aria-hidden="true" />}
          </p>

          {searchable && (
            <div className="sidenav-account-search">
              <span className="sidenav-account-search-icon">
                <SearchIcon />
              </span>
              <input
                ref={searchInputRef}
                type="text"
                className="sidenav-account-search-input"
                placeholder={t('account.search', 'Search accounts...')}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label={t('account.search', 'Search accounts...')}
                aria-controls={listboxId}
              />
            </div>
          )}

          {safeFilters && safeFilters.length > 0 && (
            <div
              className="sidenav-account-filters"
              role="group"
              aria-label={t('account.filters', 'Filter accounts by scope')}
            >
              {safeFilters.map((filter) => {
                const isActive = filter.id === activeFilterId;
                return (
                  <button
                    key={filter.id}
                    type="button"
                    className={`sidenav-account-filter ${
                      isActive ? 'sidenav-account-filter--active' : ''
                    }`}
                    aria-pressed={isActive}
                    onClick={() => handleFilterChange(filter.id)}
                  >
                    {filter.tone && (
                      <span
                        className={`sidenav-account-filter-dot sidenav-account-filter-dot--${filter.tone}`}
                        aria-hidden="true"
                      />
                    )}
                    {filter.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {renderListContent()}
      </div>
    );

    // Flotante (sidebar colapsado): en un portal sobre la página, para que el
    // stacking context del Sidenav (z-index 40) no lo recorte ni lo tape.
    return isFloating
      ? inPortal(<div className={`sidenav-portal ${portalClassName}`.trim()}>{panel}</div>)
      : panel;
  };

  return (
    <div
      className={`sidenav-account ${className}`.trim()}
      onKeyDown={handleKeyDown}
    >
      {!isCollapsed && !showSkeleton && (
        <p className="sidenav-account-label">
          {t('account.label', 'Selected Account')}
        </p>
      )}

      {renderTrigger()}
      {renderPanel()}
    </div>
  );
};

export default AccountSelector;
